import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { History, Inbox, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api, errText } from "@/lib/api";
import EventDialog from "@/components/EventDialog";
import { EntryActionsDialog } from "@/components/EntryActionsDialog";
import { MatterCombobox } from "@/components/MatterCombobox";
import { longDate, whenLabel } from "@/lib/eventMeta";
import { useAuth } from "@/context/AuthContext";

const groupByDate = (list) => {
  const groups = new Map();
  for (const ev of list) {
    if (!groups.has(ev.date)) groups.set(ev.date, []);
    groups.get(ev.date).push(ev);
  }
  return [...groups.entries()];
};

const Row = ({ event, matters, canWrite, onLink, onOpen }) => (
  <div
    data-testid={`uncat-row-${event.event_id}`}
    className="flex flex-col md:flex-row md:items-center gap-3 border border-border rounded-sm px-4 py-3"
    style={{ borderLeft: `5px solid ${event.court_color || event.color || "rgb(var(--c-session))"}` }}
  >
    <div className="min-w-0 flex-1">
      <p className="text-base font-semibold leading-snug">{event.title}</p>
      <p className="mt-1 text-xs text-muted-foreground">
        {[whenLabel(event), event.court_name, event.location].filter(Boolean).join(" · ")}
      </p>
    </div>
    <div className="w-full md:w-72 shrink-0">
      <MatterCombobox
        matters={matters}
        value={event.matter_id}
        onChange={(id) => id && onLink(event, id)}
        testId={`uncat-matter-${event.event_id}`}
        emptyLabel="Link a matter"
        height="h-10"
        disabled={!canWrite}
      />
    </div>
    <Button
      variant="outline"
      data-testid={`uncat-open-${event.event_id}`}
      onClick={() => onOpen(event)}
      className="rounded-full h-10 px-4 shrink-0"
    >
      <Pencil className="h-4 w-4 mr-2" /> Open
    </Button>
  </div>
);

export default function Uncategorized() {
  const { canWrite, workspace } = useAuth();
  const [data, setData] = useState(null);
  const [matters, setMatters] = useState([]);
  const [showPast, setShowPast] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [menuEvent, setMenuEvent] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const [u, m] = await Promise.all([
        api.get("/events/uncategorized", { params: { include_past: true } }),
        api.get("/matters"),
      ]);
      setData(u.data);
      setMatters(m.data);
    } catch (e) {
      toast.error(errText(e));
    }
  }, [workspace?.workspace_id]);

  useEffect(() => {
    load();
  }, [load]);

  const link = async (event, matterId) => {
    try {
      await api.patch(`/events/${event.event_id}/matter`, { matter_id: matterId });
      const matter = matters.find((m) => m.matter_id === matterId);
      toast.success(`Linked to ${matter?.title || "the matter"}`);
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const upcomingGroups = useMemo(() => groupByDate(data?.upcoming || []), [data]);
  const pastGroups = useMemo(() => groupByDate(data?.past || []), [data]);

  if (!data) return <div className="h-64 surface animate-pulse" data-testid="uncat-loading" />;

  return (
    <div className="space-y-8" data-testid="uncategorized-page">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div className="min-w-0">
          <p className="label-caps inline-flex items-center gap-2">
            <Inbox className="h-4 w-4" /> Uncategorized
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold mt-2">
            {data.counts.upcoming} entr{data.counts.upcoming === 1 ? "y" : "ies"} with no matter
          </h1>
          <p className="mt-3 text-sm text-muted-foreground max-w-2xl">
            Quick placeholders, pasted meetings and imported listings land here until you link them
            to a matter. Link one and it joins that matter's timeline, briefs and hearing logs.
          </p>
        </div>
        <label className="flex items-center gap-3 border border-border rounded-sm px-4 py-3">
          <History className="h-4 w-4" />
          <span className="text-sm font-medium">
            Show past ({data.counts.past})
          </span>
          <Switch
            data-testid="uncat-show-past"
            checked={showPast}
            onCheckedChange={setShowPast}
          />
        </label>
      </div>

      <section className="surface p-6" data-testid="uncat-upcoming">
        <h2 className="text-base md:text-lg font-heading font-semibold">Today and ahead</h2>
        <div className="mt-5 space-y-6">
          {upcomingGroups.length ? (
            upcomingGroups.map(([date, events]) => (
              <div key={date} className="space-y-2">
                <p className="label-caps">{longDate(date)}</p>
                {events.map((event) => (
                  <Row
                    key={event.event_id}
                    event={event}
                    matters={matters}
                    canWrite={canWrite}
                    onLink={link}
                    onOpen={(e) => setMenuEvent(e)}
                  />
                ))}
              </div>
            ))
          ) : (
            <p
              className="text-sm text-muted-foreground border border-dashed border-border rounded-sm px-4 py-6"
              data-testid="uncat-upcoming-empty"
            >
              Everything ahead is linked to a matter. Nicely kept.
            </p>
          )}
        </div>
      </section>

      {showPast ? (
        <section className="surface p-6" data-testid="uncat-past">
          <h2 className="text-base md:text-lg font-heading font-semibold">Earlier entries</h2>
          <div className="mt-5 space-y-6">
            {pastGroups.length ? (
              pastGroups.map(([date, events]) => (
                <div key={date} className="space-y-2">
                  <p className="label-caps">{longDate(date)}</p>
                  {events.map((event) => (
                    <Row
                      key={event.event_id}
                      event={event}
                      matters={matters}
                      canWrite={canWrite}
                      onLink={link}
                      onOpen={(e) => setMenuEvent(e)}
                    />
                  ))}
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground" data-testid="uncat-past-empty">
                Nothing unlinked in the past.
              </p>
            )}
          </div>
        </section>
      ) : null}

      <EntryActionsDialog
        event={menuEvent}
        onOpenChange={(v) => !v && setMenuEvent(null)}
        onEdit={(ev) => {
          setMenuEvent(null);
          setEditing(ev);
          setDialogOpen(true);
        }}
        onChanged={load}
      />

      <EventDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        event={editing}
        defaultDate={editing?.date}
        onSaved={load}
      />
    </div>
  );
}
